import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
    "Chauffeur - Worldwide",
    "Private Aviation",
    "Yachts & Charter",
    "Pet Relocation",
    "Vehicle Relocation",
    "Lifestyle Concierge",
    "Something else entirely",
];

const TEXTURE =
    "https://static.prod-images.emergentagent.com/jobs/518f9d69-b3b1-4efc-8aee-9f5aecd83221/images/2e4e88aa735776999c5a7417b9d6bf9e0b10651d41b1f29d096d9433f6fdb84f.png";

export default function EnquiryForm() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        service: SERVICES[0],
        date: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Name, email and message are required.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/enquiries`, form);
            setDone(true);
            toast.success("Your request has been received. Colin will be in touch personally.");
            setForm({
                name: "",
                email: "",
                phone: "",
                service: SERVICES[0],
                date: "",
                message: "",
            });
        } catch (err) {
            const detail =
                err?.response?.data?.detail?.[0]?.msg ||
                err?.response?.data?.detail ||
                "Something prevented your enquiry from reaching us. Please try again.";
            toast.error(typeof detail === "string" ? detail : "Submission failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="enquire"
            data-testid="enquiry-section"
            className="relative py-24 md:py-40 overflow-hidden bg-cream"
        >
            <div
                className="absolute inset-0 opacity-25"
                style={{
                    backgroundImage: `url(${TEXTURE})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            />
            <div className="absolute inset-0 bg-cream/85" />

            <div className="relative max-w-3xl mx-auto px-6 md:px-12 bg-cream/95 md:bg-transparent">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1 }}
                    className="text-center"
                >
                    <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-oxblood">
                        № V - Begin
                    </span>
                    <h2 className="mt-6 font-serif text-[2.4rem] md:text-[3.4rem] leading-[1.05]">
                        A bespoke <span className="italic">enquiry.</span>
                    </h2>
                    <p className="mt-5 font-sans text-[14px] md:text-[15px] text-[#1A1A1A]/70 leading-[1.85] max-w-xl mx-auto">
                        Share a few details. Every request is read personally, in confidence,
                        and answered within twenty-four hours.
                    </p>
                </motion.div>

                {done ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        data-testid="enquiry-success"
                        className="mt-16 border border-bone bg-cream px-8 md:px-14 py-14 text-center"
                    >
                        <div className="font-serif italic text-oxblood text-[22px]">
                            Thank you.
                        </div>
                        <p className="mt-4 font-sans text-[14px] text-[#1A1A1A]/75 leading-[1.85]">
                            Your enquiry has been received. Colin will respond to you
                            personally, by the means you've shared, within twenty-four hours.
                        </p>
                        <button
                            onClick={() => setDone(false)}
                            data-testid="enquiry-new-btn"
                            className="mt-8 font-sans text-[11px] uppercase tracking-[0.3em] text-oxblood border-b border-oxblood pb-1 hover:opacity-70 transition"
                        >
                            Make another enquiry
                        </button>
                    </motion.div>
                ) : (
                    <motion.form
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.15 }}
                        onSubmit={submit}
                        data-testid="enquiry-form"
                        className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2"
                    >
                        <Field label="Full name">
                            <input
                                className="line-input"
                                type="text"
                                value={form.name}
                                onChange={update("name")}
                                placeholder="Mr / Mrs / Hon."
                                disabled={submitting}
                                data-testid="enquiry-name"
                                required
                            />
                        </Field>
                        <Field label="Email">
                            <input
                                className="line-input"
                                type="email"
                                value={form.email}
                                onChange={update("email")}
                                placeholder="you@private.com"
                                disabled={submitting}
                                data-testid="enquiry-email"
                                required
                            />
                        </Field>
                        <Field label="Telephone">
                            <input
                                className="line-input"
                                type="tel"
                                value={form.phone}
                                onChange={update("phone")}
                                placeholder="+44 …"
                                disabled={submitting}
                                data-testid="enquiry-phone"
                            />
                        </Field>
                        <Field label="Preferred date">
                            <input
                                className="line-input"
                                type="text"
                                value={form.date}
                                onChange={update("date")}
                                placeholder="e.g. 14 - 17 Feb"
                                disabled={submitting}
                                data-testid="enquiry-date"
                            />
                        </Field>
                        <Field label="Service" className="md:col-span-2">
                            <select
                                className="line-input appearance-none cursor-pointer"
                                value={form.service}
                                onChange={update("service")}
                                disabled={submitting}
                                data-testid="enquiry-service"
                            >
                                {SERVICES.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Your request" className="md:col-span-2">
                            <textarea
                                className="line-input"
                                rows={4}
                                value={form.message}
                                onChange={update("message")}
                                placeholder="Tell us what you would like. The detail is the discretion."
                                disabled={submitting}
                                data-testid="enquiry-message"
                                required
                            />
                        </Field>

                        <div className="md:col-span-2 mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                            <p className="font-sans text-[11px] tracking-[0.18em] uppercase text-[#595959]">
                                Handled in strict confidence
                            </p>
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="enquiry-submit"
                                className="group relative inline-flex items-center justify-center bg-oxblood text-[#F9F8F6] font-sans text-[11px] uppercase tracking-[0.32em] px-10 py-5 hover:bg-[#4A1410] transition-colors duration-500 disabled:opacity-60"
                            >
                                {submitting ? "Sending…" : "Submit enquiry"}
                                <span className="ml-3 transition-transform duration-500 group-hover:translate-x-1">
                                    →
                                </span>
                            </button>
                        </div>
                    </motion.form>
                )}
            </div>
        </section>
    );
}

function Field({ label, children, className = "" }) {
    return (
        <label className={`block py-4 ${className}`}>
            <span className="block font-sans text-[10px] uppercase tracking-[0.32em] text-[#595959] mb-1">
                {label}
            </span>
            {children}
        </label>
    );
}
